import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Scale, Menu, X } from "lucide-react";

const LINKS = [
  { label: "Product", href: "#preview" },
  { label: "Features", href: "#features" },
  { label: "Pricing", href: "/pricing" },
  { label: "FAQ", href: "#faq" },
];

export const LandingNav = () => {
  const [open, setOpen] = useState(false);
  const [solid, setSolid] = useState(false);

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="landing-nav"
      className={`sticky top-0 z-50 border-b bg-white/85 backdrop-blur-xl transition-shadow ${
        solid ? "border-border shadow-sm" : "border-border/60"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-brand" className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-navy text-gold">
            <Scale className="h-5 w-5" strokeWidth={1.75} />
          </span>
          <span className="leading-none">
            <span className="block font-display text-xl tracking-tight text-navy">Advocate Diary</span>
            <span className="hidden text-[11px] text-muted-foreground sm:block">
              Practice management for Indian advocates
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) =>
            l.href.startsWith("#") ? (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm text-navy/70 transition-colors hover:text-navy"
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm text-navy/70 transition-colors hover:text-navy"
              >
                {l.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <Link
            to="/login"
            data-testid="nav-login-btn"
            className="rounded-md px-4 py-2 text-sm font-medium text-navy transition-colors hover:bg-secondary"
          >
            Sign in
          </Link>
          <Link
            to="/register"
            data-testid="nav-register-btn"
            className="rounded-md bg-gold px-4 py-2 text-sm font-semibold text-navy shadow-sm transition-all hover:-translate-y-0.5 hover:bg-gold-light"
          >
            Start free
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          className="rounded-md p-2 text-navy md:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="border-t border-border bg-white px-4 py-4 md:hidden">
          <div className="flex flex-col gap-1">
            {LINKS.map((l) =>
              l.href.startsWith("#") ? (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="rounded-md px-2 py-2.5 text-sm text-navy/80 hover:bg-secondary"
                >
                  {l.label}
                </a>
              ) : (
                <Link
                  key={l.href}
                  to={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="rounded-md px-2 py-2.5 text-sm text-navy/80 hover:bg-secondary"
                >
                  {l.label}
                </Link>
              )
            )}
          </div>
          <div className="mt-3 flex flex-col gap-2 border-t border-border pt-3">
            <Link
              to="/login"
              data-testid="nav-mobile-login-btn"
              className="rounded-md border border-border px-4 py-2.5 text-center text-sm font-medium text-navy"
            >
              Sign in
            </Link>
            <Link
              to="/register"
              data-testid="nav-mobile-register-btn"
              className="rounded-md bg-gold px-4 py-2.5 text-center text-sm font-semibold text-navy"
            >
              Start free
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
